/**
 * Global Chart Toolbar â€” shared horizontal bar above all chart panes.
 *
 * Contains: timeframe, chart type, indicators, templates, zoom, screenshot,
 * alerts, strategy, custom indicator, replay, layout, settings.
 * Drawing tools have been moved to ChartToolsSidebar (vertical left panel).
 */

import React, { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { setTimeframe, setChartType, toggleMarketWatch, setLayoutMode, toggleToolsSidebar } from "../../store/chart-slice";
import { ChartPanel as ChartPanelType, TimeframeType, BUILT_IN_TIMEFRAMES, getTimeframeConfig, registerCustomTimeframe, ChartType, CHART_TYPE_LABELS, LayoutMode } from "../../types/chart.types";
import { ActiveIndicator } from "../../indicators/types/indicator.types";
import IndicatorPanel from "./IndicatorPanel";
import TemplateMenu from "./TemplateMenu";
import { ChartPanelHandle } from "./ChartPanel";
import { DrawingTool } from "../../types/drawing.types";
import { getAmibrokerEnabled, loadChartSettings, loadCustomTimeframes, saveAmibrokerEnabled } from "../../service/local-storage-handler";
import { syncSaveChartSettings } from "../../service/sync-handler";
import { themeReducer } from "../../store/theme-slice";
import { logout } from "@service/user-handler";
import { MdLogout } from "react-icons/md";
import { LiaDigitalTachographSolid } from "react-icons/lia";
import { BsPersonLock, BsMoonStars, BsSun, BsGear, BsClock } from "react-icons/bs";
import NotificationBell from "../navbar/NotificationBell";
import LogoutConfirmModal from "@components/logout-confirm-modal/LogoutConfirmModal";
import CustomTimeframeModal from "./CustomTimeframeModal";
import { isMobile, isDesktop } from "@platform/index";
import { setAmibrokerEnabled, setAmibrokerInitialized } from "@store/amibroker-slice";
import { notifyAmibrokerLogin, notifyAmibrokerLogout, pushSymbolScripIdMap } from "@service/ipc-handler";
import { createWatchlistInAmibrokerHandler } from "@service/watchlist-handler";
import toast from "react-hot-toast";
import "./GlobalChartToolbar.scss";

const TIMEFRAMES: TimeframeType[] = [...BUILT_IN_TIMEFRAMES];

// â”€â”€ Chart type icons â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
function chartTypeIcon(type: ChartType, size = 20): React.ReactNode {
  switch (type) {
    case "candlestick":
      return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="6" y="4" width="4" height="16" rx="0.5" /><line x1="8" y1="2" x2="8" y2="4" /><line x1="8" y1="20" x2="8" y2="22" /><rect x="14" y="8" width="4" height="8" rx="0.5" /><line x1="16" y1="5" x2="16" y2="8" /><line x1="16" y1="16" x2="16" y2="19" /></svg>);
    case "line":
      return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="3,17 8,10 13,14 21,5" strokeLinecap="round" strokeLinejoin="round" /></svg>);
    case "area":
      return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5"><polygon points="3,17 8,10 13,14 21,5 21,20 3,20" fill="currentColor" opacity="0.15" stroke="none" /><polyline points="3,17 8,10 13,14 21,5" strokeLinecap="round" strokeLinejoin="round" /></svg>);
    case "baseline":
      return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="2" y1="12" x2="22" y2="12" strokeDasharray="2 2" opacity="0.4" /><polyline points="3,15 7,8 11,14 15,6 19,10 21,9" strokeLinecap="round" strokeLinejoin="round" /></svg>);
    case "bar":
      return (<svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.5"><line x1="7" y1="3" x2="7" y2="21" /><line x1="4" y1="8" x2="7" y2="8" /><line x1="7" y1="16" x2="10" y2="16" /><line x1="17" y1="5" x2="17" y2="19" /><line x1="14" y1="10" x2="17" y2="10" /><line x1="17" y1="14" x2="20" y2="14" /></svg>);
    default:
      return null;
  }
}

// â”€â”€ Props â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
interface GlobalChartToolbarProps {
  activePanelId: string | null;
  activePanelRef: React.RefObject<ChartPanelHandle | null>;
  colorTheme: string;
  activeTool: DrawingTool;
  onToolChange: (tool: DrawingTool) => void;
  activeIndicatorsVisible: boolean;
  onToggleActiveIndicators: () => void;
  onToolsClick?: () => void;
  toolsOpen?: boolean;
}

const GlobalChartToolbar: React.FC<GlobalChartToolbarProps> = ({
  activePanelId,
  activePanelRef,
  colorTheme,
  activeTool,
  onToolChange,
  activeIndicatorsVisible,
  onToggleActiveIndicators,
  onToolsClick,
  toolsOpen,
}) => {
  const dispatch = useDispatch();
  const hasTauriInvoke = typeof (window as any)?.__TAURI_INTERNALS__?.invoke === "function";
  const showAmiBrokerControls = isDesktop && hasTauriInvoke;

  // Read the active panel's data from Redux
  const activePanel = useSelector((state: any) =>
    (state.chart?.panels || []).find((p: ChartPanelType) => p.id === activePanelId) || null
  );
  const marketWatchOpen = useSelector((state: any) => state.chart?.marketWatchOpen || false);
  const toolsSidebarOpen = useSelector((state: any) => state.chart?.toolsSidebarOpen ?? false);
  const layoutMode: LayoutMode = useSelector((state: any) => state.chart?.layoutMode || "tab");
  const amibrokerEnabled: boolean = useSelector((state: any) => state.amibroker?.isEnabled || false);
  const amibrokerInitialized: boolean = useSelector((state: any) => state.amibroker?.isInitialized || false);

  const symbolScripIdMap: Record<string, string> = useSelector((state: any) => state.metadata?.feedsScripsState?.symbolScripId || {});
  const feedsScrips: any[] = useSelector((state: any) => state.metadata?.feedsScripsState?.feedsScrips || []);
  const userWatchlist: any[] = useSelector((state: any) => state.metadata?.marketWatchState?.userWatchlist || []);
  const defaultWatchlist: any[] = useSelector((state: any) => state.metadata?.marketWatchState?.defaultWatchlist || []);
  const autoSyncRef = useRef<string>("");
  const amiInitializationRef = useRef(false);

  useEffect(() => {
    if (showAmiBrokerControls && getAmibrokerEnabled()) {
      dispatch(setAmibrokerEnabled(true));
    }
  }, [dispatch, showAmiBrokerControls]);

  const syncAmibrokerCatalog = useCallback(async () => {
    if (!showAmiBrokerControls || !amibrokerEnabled || !amibrokerInitialized) return;

    const symbolCount = Object.keys(symbolScripIdMap).length;
    const feedCount = feedsScrips.length;
    const userCount = userWatchlist.length;
    const defaultCount = defaultWatchlist.length;
    if (symbolCount === 0 && feedCount === 0 && userCount === 0 && defaultCount === 0) return;

    try {
      if (symbolCount > 0) {
        await pushSymbolScripIdMap(symbolScripIdMap);
      }

      await createWatchlistInAmibrokerHandler({
        feedsScrips,
        userWatchlist,
        defaultWatchlist,
      });
    } catch (err) {
      console.error("[AmiBroker] Auto-sync failed:", err);
    }
  }, [
    showAmiBrokerControls,
    amibrokerEnabled,
    amibrokerInitialized,
    symbolScripIdMap,
    feedsScrips,
    userWatchlist,
    defaultWatchlist,
  ]);
  // â”€â”€ Layout dropdown â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [layoutDropdownOpen, setLayoutDropdownOpen] = useState(false);
  const layoutDropdownRef = useRef<HTMLDivElement>(null);
  const layoutBtnRef = useRef<HTMLButtonElement>(null);
  const [layoutAnchorRect, setLayoutAnchorRect] = useState<DOMRect | null>(null);
  const handleLayoutChange = (mode: LayoutMode) => dispatch(setLayoutMode(mode));

  // â”€â”€ Settings dropdown â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsRef = useRef<HTMLDivElement>(null);
  const settingsBtnRef = useRef<HTMLButtonElement>(null);
  const settingsPortalRef = useRef<HTMLDivElement>(null);
  const [settingsAnchorRect, setSettingsAnchorRect] = useState<DOMRect | null>(null);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showTimeframeModal, setShowTimeframeModal] = useState(false);

  const toggleTheme = () => {
    const newTheme = colorTheme === "light" ? "dark" : "light";
    dispatch(themeReducer({ theme: newTheme }));
  };

  const handleLogoutClick = () => { setSettingsOpen(false); setShowLogoutModal(true); };
  const handleLogoutConfirm = async () => {
    setShowLogoutModal(false);
    try {
      await logout();
    } catch (e) {
      console.error("Logout failed:", e);
    }
  };
  const handleLogoutCancel = () => setShowLogoutModal(false);

  // -- AmiBroker toggle handler
  const handleAmiToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    dispatch(setAmibrokerEnabled(enabled));
    saveAmibrokerEnabled(enabled);
    if (enabled) {
      amiInitializationRef.current = true;
      const result: any = await notifyAmibrokerLogin();
      if (result?.__invokeError != null) {
        // invoke threw — show the actual Rust/Tauri error
        toast.error(`AmiBroker: ${result.__invokeError}`);
        dispatch(setAmibrokerEnabled(false));
        saveAmibrokerEnabled(false);
      } else if (!result?.isAmibrokerInstalled) {
        toast.error("AmiBroker is not installed on this machine");
        dispatch(setAmibrokerEnabled(false));
        saveAmibrokerEnabled(false);
      } else if (!result?.isInitialized) {
        const errDetail = result?.error ? `: ${result.error}` : "";
        toast.error(`AmiBroker failed to initialize${errDetail}`);
        dispatch(setAmibrokerEnabled(false));
        saveAmibrokerEnabled(false);
      } else {
        dispatch(setAmibrokerInitialized(true));
        toast.success("AmiBroker connected");
        // Initial sync immediately; if metadata is still loading, the effect
        // below will auto-sync again as soon as data becomes available.
        await syncAmibrokerCatalog();
      }
      amiInitializationRef.current = false;
    } else {
      dispatch(setAmibrokerInitialized(false));
      autoSyncRef.current = "";
      await notifyAmibrokerLogout();
    }
  };

  useEffect(() => {
    if (
      !showAmiBrokerControls ||
      !amibrokerEnabled ||
      amibrokerInitialized ||
      amiInitializationRef.current
    ) {
      return;
    }

    amiInitializationRef.current = true;
    void notifyAmibrokerLogin()
      .then((result: any) => {
        if (result?.isInitialized) {
          dispatch(setAmibrokerInitialized(true));
        } else {
          dispatch(setAmibrokerEnabled(false));
          saveAmibrokerEnabled(false);
          const error = result?.__invokeError || result?.error;
          if (error) toast.error(`AmiBroker failed to initialize: ${error}`);
        }
      })
      .finally(() => {
        amiInitializationRef.current = false;
      });
  }, [amibrokerEnabled, amibrokerInitialized, dispatch, showAmiBrokerControls]);

  useEffect(() => {
    if (!showAmiBrokerControls || !amibrokerEnabled || !amibrokerInitialized) {
      autoSyncRef.current = "";
      return;
    }

    const key = [
      Object.keys(symbolScripIdMap).length,
      feedsScrips.length,
      userWatchlist.length,
      defaultWatchlist.length,
    ].join("|");

    if (autoSyncRef.current === key) return;
    autoSyncRef.current = key;

    void syncAmibrokerCatalog();
  }, [
    showAmiBrokerControls,
    amibrokerEnabled,
    amibrokerInitialized,
    symbolScripIdMap,
    feedsScrips,
    userWatchlist,
    defaultWatchlist,
    syncAmibrokerCatalog,
  ]);

  // Close layout / settings dropdowns on outside click
  useEffect(() => {
    if (!settingsOpen && !layoutDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        settingsOpen &&
        settingsBtnRef.current && !settingsBtnRef.current.contains(t) &&
        settingsPortalRef.current && !settingsPortalRef.current.contains(t)
      ) {
        setSettingsOpen(false);
      }
      if (
        layoutDropdownOpen &&
        layoutBtnRef.current && !layoutBtnRef.current.contains(t) &&
        layoutDropdownRef.current && !layoutDropdownRef.current.contains(t)
      ) {
        setLayoutDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [settingsOpen, layoutDropdownOpen]);

  // â”€â”€ Timeframe dropdown â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [isTimeframeOpen, setIsTimeframeOpen] = useState(false);
  const timeframeDropdownRef = useRef<HTMLDivElement>(null);
  const [tfAnchorRect, setTfAnchorRect] = useState<DOMRect | null>(null);
  const tfPortalRef = useRef<HTMLDivElement>(null);

  const handleTimeframeChange = useCallback((tf: TimeframeType) => {
    if (activePanelId) {
      dispatch(setTimeframe({ panelId: activePanelId, timeframe: tf }));
    }
    setIsTimeframeOpen(false);
  }, [dispatch, activePanelId]);

  useEffect(() => {
    if (!isTimeframeOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        timeframeDropdownRef.current && !timeframeDropdownRef.current.contains(e.target as Node) &&
        !(tfPortalRef.current && tfPortalRef.current.contains(e.target as Node))
      ) {
        setIsTimeframeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isTimeframeOpen]);

  // â”€â”€ Chart type dropdown â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [isChartTypeOpen, setIsChartTypeOpen] = useState(false);
  const chartTypeDropdownRef = useRef<HTMLDivElement>(null);
  const [ctAnchorRect, setCtAnchorRect] = useState<DOMRect | null>(null);
  const ctPortalRef = useRef<HTMLDivElement>(null);

  const handleChartTypeChange = useCallback((type: ChartType) => {
    if (activePanelId) {
      dispatch(setChartType({ panelId: activePanelId, chartType: type }));
    }
    setIsChartTypeOpen(false);
  }, [dispatch, activePanelId]);

  useEffect(() => {
    if (!isChartTypeOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        chartTypeDropdownRef.current && !chartTypeDropdownRef.current.contains(e.target as Node) &&
        !(ctPortalRef.current && ctPortalRef.current.contains(e.target as Node))
      ) {
        setIsChartTypeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isChartTypeOpen]);

  // â”€â”€ Grid visibility toggle â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [gridVisible, setGridVisible] = useState(() => loadChartSettings().gridVisible);

  const handleToggleGrid = useCallback(() => {
    const next = !gridVisible;
    setGridVisible(next);
    const settings = loadChartSettings();
    syncSaveChartSettings({ ...settings, gridVisible: next });
    const h = activePanelRef.current;
    if (h) h.toggleGrid(next);
  }, [gridVisible, activePanelRef]);

  // â”€â”€ Indicator state from active pane (read via ref) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [activeIndicators, setActiveIndicators] = useState<ActiveIndicator[]>([]);

  // Poll active pane state periodically to keep toolbar in sync
  useEffect(() => {
    const sync = () => {
      const handle = activePanelRef.current;
      if (handle) {
        setActiveIndicators(handle.getActiveIndicators());
      } else {
        setActiveIndicators([]);
      }
    };
    sync();
    const id = setInterval(sync, 500);
    return () => clearInterval(id);
  }, [activePanelRef, activePanelId]);

  // â”€â”€ Delegate actions to active pane via ref â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const callRef = useCallback((fn: (h: ChartPanelHandle) => void) => {
    const h = activePanelRef.current;
    if (h) fn(h);
  }, [activePanelRef]);

  const currentTf = activePanel?.timeframe || "1";
  const currentChartType: ChartType = activePanel?.chartType || "candlestick";

  // â”€â”€ Custom timeframes: load on mount + listen for changes â”€â”€â”€â”€â”€â”€
  const [customTfKeys, setCustomTfKeys] = useState<string[]>([]);
  const allPanels: ChartPanelType[] = useSelector((state: any) => state.chart?.panels || []);

  const refreshCustomTimeframes = useCallback(() => {
    const saved = loadCustomTimeframes();
    saved.forEach(ct => registerCustomTimeframe(ct.key, { minutes: ct.minutes, label: ct.label }));
    const newKeys = saved.map(ct => ct.key);
    setCustomTfKeys(newKeys);

    // If any panel uses a deleted custom timeframe, reset it to 1m
    const validKeys = new Set([...BUILT_IN_TIMEFRAMES, ...newKeys]);
    allPanels.forEach(p => {
      if (p.timeframe && !validKeys.has(p.timeframe)) {
        dispatch(setTimeframe({ panelId: p.id, timeframe: "1" }));
      }
    });
  }, [allPanels, dispatch]);

  useEffect(() => {
    refreshCustomTimeframes();
    window.addEventListener("custom-timeframes-changed", refreshCustomTimeframes);
    return () => window.removeEventListener("custom-timeframes-changed", refreshCustomTimeframes);
  }, [refreshCustomTimeframes]);

  const allTimeframes = [...TIMEFRAMES, ...customTfKeys].sort(
    (a, b) => getTimeframeConfig(a).minutes - getTimeframeConfig(b).minutes
  );

  // â”€â”€ Toolbar ref (for dropdown portal positioning) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const toolbarRef = useRef<HTMLDivElement>(null);

  return (
  <>
    <div ref={toolbarRef} className={`global-chart-toolbar ${colorTheme}`}>

      {/* â”€â”€ NAV GROUP (Market Watch Â· Timeframe Â· ChartType) â”€â”€â”€â”€ */}
      {/* â”€â”€ Market Watch â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="toolbar-section">
        <button
          className={`toolbar-btn toolbar-text-btn${marketWatchOpen ? " toggled" : ""}`}
          onClick={() => dispatch(toggleMarketWatch())}
          title="Market Watch (Ctrl+M)"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="7" height="18" rx="1" />
            <line x1="3" y1="9" x2="10" y2="9" />
            <line x1="3" y1="14" x2="10" y2="14" />
            <line x1="13" y1="7" x2="21" y2="7" />
            <line x1="13" y1="12" x2="21" y2="12" />
            <line x1="13" y1="17" x2="18" y2="17" />
          </svg>
          <span className="toolbar-market-watch-label">Market Watch</span>
        </button>
        <button
          className={`toolbar-btn toolbar-text-btn${toolsSidebarOpen ? " toggled" : ""}`}
          onClick={() => dispatch(toggleToolsSidebar())}
          title="Drawing Tools (Ctrl+T)"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="3" y1="21" x2="10" y2="14" strokeLinecap="round" />
            <path d="M14 3l7 7-10 10-7-7z" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 7l7 7" strokeLinecap="round" />
          </svg>
          <span className="toolbar-market-watch-label">Tools</span>
        </button>
      </div>
      <div className="toolbar-sep" />

      {/* â”€â”€ Timeframe â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="toolbar-section">
        <div className="tf-selector" ref={timeframeDropdownRef}>
          <button className="toolbar-btn tf-btn" onClick={() => {
            const rect = timeframeDropdownRef.current?.getBoundingClientRect() ?? null;
            setTfAnchorRect(rect);
            setIsTimeframeOpen(v => !v);
          }}>
            {getTimeframeConfig(currentTf).label}
            <svg className="dropdown-arrow" viewBox="0 0 10 6" width="10" height="6" fill="currentColor"><path d="M0 0l5 6 5-6z"/></svg>
          </button>
        </div>
        {isTimeframeOpen && tfAnchorRect && createPortal(
          <div ref={tfPortalRef} className="toolbar-dropdown tf-dropdown"
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              position: "fixed",
              top: tfAnchorRect.bottom + 2,
              left: tfAnchorRect.left,
              maxHeight: "60vh",
              overflowY: "auto",
              zIndex: 99999,
            }}>
            {allTimeframes.map(tf => (
              <div key={tf} className={`dropdown-option ${currentTf === tf ? "active" : ""}`}
                onClick={() => handleTimeframeChange(tf)}>
                {getTimeframeConfig(tf).label}
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>

      <div className="toolbar-sep" />

      {/* â”€â”€ Chart Type â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
      <div className="toolbar-section">
        <div className="ct-selector" ref={chartTypeDropdownRef}>
          <button className="toolbar-btn ct-btn" onClick={() => {
            const rect = chartTypeDropdownRef.current?.getBoundingClientRect() ?? null;
            setCtAnchorRect(rect);
            setIsChartTypeOpen(v => !v);
          }}
            title={`Chart type: ${CHART_TYPE_LABELS[currentChartType]}`}>
            {chartTypeIcon(currentChartType)}
            <svg className="dropdown-arrow" viewBox="0 0 10 6" width="10" height="6" fill="currentColor"><path d="M0 0l5 6 5-6z"/></svg>
          </button>
        </div>
        {isChartTypeOpen && ctAnchorRect && createPortal(
          <div ref={ctPortalRef} className="toolbar-dropdown ct-dropdown"
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              position: "fixed",
              top: ctAnchorRect.bottom + 2,
              left: ctAnchorRect.left,
              zIndex: 99999,
            }}>
            {(Object.keys(CHART_TYPE_LABELS) as ChartType[]).filter(t => t !== "baseline").map(type => (
              <div key={type} className={`dropdown-option ${currentChartType === type ? "active" : ""}`}
                onClick={() => handleChartTypeChange(type)}>
                <span className="option-icon">{chartTypeIcon(type, 18)}</span>
                <span>{CHART_TYPE_LABELS[type]}</span>
              </div>
            ))}
          </div>,
          document.body
        )}
      </div>

      <div className="toolbar-sep" />

      {/* Indicators */}
      <div className="toolbar-section">
        <IndicatorPanel
          activeIndicators={activeIndicators}
          onAdd={(id) => callRef(h => h.addIndicator(id))}
          onRemove={(id) => callRef(h => h.removeIndicator(id))}
          onRemoveAll={() => callRef(h => h.removeAllIndicators())}
          onToggleVisibility={(id) => callRef(h => h.toggleIndicatorVisibility(id))}
          onParamsChange={(id, params) => callRef(h => h.changeIndicatorParams(id, params))}
          colorTheme={colorTheme}
          compact={true}
        />
      </div>

      <div className="toolbar-sep" />

      {/* Templates */}
      <div className="toolbar-section">
        <TemplateMenu
          activePanelRef={activePanelRef}
          activePanelId={activePanelId}
          colorTheme={colorTheme}
          compact={true}
        />
        <button
          className={`toolbar-btn toolbar-text-btn toolbar-active-indicators-btn${activeIndicatorsVisible ? " toggled" : ""}`}
          onClick={onToggleActiveIndicators}
          title={activeIndicatorsVisible ? "Hide Active Indicators" : "Show Active Indicators"}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
            <circle cx="6" cy="6" r="1.6" fill="currentColor" stroke="none" />
            <circle cx="6" cy="12" r="1.6" fill="currentColor" stroke="none" />
            <circle cx="6" cy="18" r="1.6" fill="currentColor" stroke="none" />
          </svg>
          <span className="toolbar-market-watch-label">Active Indicator</span>
        </button>
      </div>

      <div className="toolbar-sep" />

      {/* Zoom + Grid + Screenshot + Go-to-latest */}
      <div className="toolbar-section action-group">
        <button className="toolbar-btn action-btn" onClick={() => callRef(h => h.zoomIn())} title="Zoom In">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth={1.5} />
            <line x1="15" y1="15" x2="21" y2="21" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
            <line x1="10" y1="7" x2="10" y2="13" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          </svg>
        </button>
        <button className="toolbar-btn action-btn" onClick={() => callRef(h => h.zoomOut())} title="Zoom Out">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth={1.5} />
            <line x1="15" y1="15" x2="21" y2="21" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
            <line x1="7" y1="10" x2="13" y2="10" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" />
          </svg>
        </button>
        <button className={`toolbar-btn action-btn${gridVisible ? " toggled" : ""}`} onClick={handleToggleGrid} title={gridVisible ? "Hide Grid Lines" : "Show Grid Lines"}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
            <line x1="3" y1="15" x2="21" y2="15" />
            <line x1="9" y1="3" x2="9" y2="21" />
            <line x1="15" y1="3" x2="15" y2="21" />
          </svg>
        </button>
        <button className="toolbar-btn action-btn" onClick={() => callRef(h => h.scrollToLatest())} title="Go to latest candle">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <polygon points="6,5 17,12 6,19" fill="currentColor" />
            <rect x="18" y="5" width="2.5" height="14" rx="0.5" fill="currentColor" />
          </svg>
        </button>
        <button className="toolbar-btn action-btn" onClick={() => callRef(h => h.takeScreenshot())} title="Screenshot to clipboard">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="5" width="18" height="14" rx="2" />
            <circle cx="12" cy="12" r="3.5" />
            <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" />
            <rect x="14" y="3" width="4" height="2" rx="0.5" />
          </svg>
        </button>
      </div>

      <div className="toolbar-sep" />

      {/* Alerts + Compare */}
      <div className="toolbar-section action-group">
        <button className="toolbar-btn action-btn" onClick={() => callRef(h => h.toggleAlerts())} title="Price Alerts">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <button className="toolbar-btn action-btn" onClick={() => callRef(h => h.toggleCompare())} title="Compare Symbol">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5">
            <polyline points="3,17 8,11 13,14 21,6" strokeLinecap="round" strokeLinejoin="round" />
            <polyline points="3,21 9,15 14,18 21,12" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="3 2" />
          </svg>
        </button>
      </div>

      {/* Spacer: pushes Layout/Bell/Settings to far right */}
      <div className="tb-spacer" />

      {showAmiBrokerControls && amibrokerEnabled && (
        <button
          className="toolbar-btn action-btn toolbar-text-btn"
          onClick={() => window.dispatchEvent(new CustomEvent("open-amibroker-modal"))}
          title="AmiBroker Watchlist"
        >
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="9" x2="9" y2="21" />
          </svg>
          {/* eslint-disable-next-line react/forbid-dom-props */}
          <span style={{ marginLeft: 4, fontSize: 12 }}>Watchlist</span>
        </button>
      )}

      {showAmiBrokerControls && (
        <div className="toolbar-ami-toggle" title="Enable/disable AmiBroker integration">
          <span className="toolbar-ami-label">AmiBroker</span>
          <label className="ami-toggle-label" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={amibrokerEnabled}
              onChange={handleAmiToggle}
              aria-label="Enable AmiBroker integration"
            />
            <span className="ami-toggle-slider" />
          </label>
        </div>
      )}

      <div className="toolbar-dropdown-wrapper">
        <button
          ref={layoutBtnRef}
          className={`toolbar-btn action-btn${layoutDropdownOpen ? " toggled" : ""}`}
          onClick={() => {
            const rect = layoutBtnRef.current?.getBoundingClientRect() ?? null;
            setLayoutAnchorRect(rect);
            setLayoutDropdownOpen(v => !v);
          }}
          title="Chart Layout"
        >
          {layoutMode === "grid" && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg>}
          {layoutMode === "horizontal" && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="5" rx="1" /><rect x="3" y="10" width="18" height="5" rx="1" /><rect x="3" y="17" width="18" height="4" rx="1" /></svg>}
          {layoutMode === "vertical" && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="18" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" /></svg>}
          {layoutMode === "tab" && <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /></svg>}
          <svg viewBox="0 0 10 6" width="8" height="5" fill="currentColor"><path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" /></svg>
        </button>
        {layoutDropdownOpen && layoutAnchorRect && createPortal(
          <div ref={layoutDropdownRef} className="toolbar-dropdown layout-dropdown"
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              position: "fixed",
              top: layoutAnchorRect.bottom + 2,
              ...(isMobile
                ? { left: layoutAnchorRect.left, right: "auto" }
                : { right: window.innerWidth - layoutAnchorRect.right, left: "auto" }),
              zIndex: 99999,
            }}>
            {([
              { mode: "tab" as const, label: "Tabbed", icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="18" rx="2" /></svg> },
              { mode: "grid" as const, label: "Grid", icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="8" height="8" rx="1" /><rect x="13" y="3" width="8" height="8" rx="1" /><rect x="3" y="13" width="8" height="8" rx="1" /><rect x="13" y="13" width="8" height="8" rx="1" /></svg> },
              { mode: "horizontal" as const, label: "Horizontal", icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="18" height="5" rx="1" /><rect x="3" y="10" width="18" height="5" rx="1" /><rect x="3" y="17" width="18" height="4" rx="1" /></svg> },
              { mode: "vertical" as const, label: "Vertical", icon: <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="5" height="18" rx="1" /><rect x="10" y="3" width="5" height="18" rx="1" /><rect x="17" y="3" width="4" height="18" rx="1" /></svg> },
            ]).map(({ mode, label, icon }) => (
              <button
                key={mode}
                className={`dropdown-option ${layoutMode === mode ? "active" : ""}`}
                onClick={() => { handleLayoutChange(mode); setLayoutDropdownOpen(false); }}
              >
                <span className="option-icon">{icon}</span>
                <span>{label}</span>
                {layoutMode === mode && (
                  <span className="dropdown-check">
                    <svg viewBox="0 0 12 10" width="12" height="10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="1,5 4,9 11,1" /></svg>
                  </span>
                )}
              </button>
            ))}
          </div>,
          document.body
        )}
      </div>

      <NotificationBell colorTheme={colorTheme} />


      <div className="toolbar-dropdown-wrapper" ref={settingsRef}>
        <button
          ref={settingsBtnRef}
          className={`toolbar-btn action-btn${settingsOpen ? " toggled" : ""}`}
          onClick={() => {
            const rect = settingsBtnRef.current?.getBoundingClientRect() ?? null;
            setSettingsAnchorRect(rect);
            setSettingsOpen(v => !v);
          }}
          title="Settings"
        >
          <BsGear size={16} />
        </button>
        {settingsOpen && settingsAnchorRect && createPortal(
          <div ref={settingsPortalRef} className="toolbar-dropdown settings-dropdown right-aligned"
            // eslint-disable-next-line react/forbid-dom-props
            style={{
              position: "fixed",
              top: settingsAnchorRect.bottom + 2,
              right: window.innerWidth - settingsAnchorRect.right,
              left: "auto",
              zIndex: 99999,
            }}>
            <button className="dropdown-option" onClick={() => { setSettingsOpen(false); window.dispatchEvent(new CustomEvent("open-dashboard-modal")); }}>
              <span className="option-icon"><LiaDigitalTachographSolid size={15} /></span>
              <span>Dashboard</span>
            </button>
            <div className="dropdown-divider" />
            <button className="dropdown-option" onClick={toggleTheme}>
              <span className="option-icon">{colorTheme === "light" ? <BsMoonStars size={15} /> : <BsSun size={15} />}</span>
              <span>Theme</span>
              <span className="dropdown-value">{colorTheme === "light" ? "Dark" : "Light"}</span>
            </button>
            <button className="dropdown-option" onClick={() => { setSettingsOpen(false); window.dispatchEvent(new CustomEvent("open-password-modal")); }}>
              <span className="option-icon"><BsPersonLock size={15} /></span>
              <span>Password</span>
            </button>
            <button className="dropdown-option" onClick={() => { setSettingsOpen(false); setShowTimeframeModal(true); }}>
              <span className="option-icon"><BsClock size={15} /></span>
              <span>Preferences</span>
            </button>
            <div className="dropdown-divider" />
            <button className="dropdown-option dropdown-option-danger" onClick={handleLogoutClick}>
              <span className="option-icon"><MdLogout size={15} /></span>
              <span>Logout</span>
            </button>
          </div>,
          document.body
        )}
      </div>

    </div>{/* end global-chart-toolbar */}

    <LogoutConfirmModal
      isOpen={showLogoutModal}
      onConfirm={handleLogoutConfirm}
      onCancel={handleLogoutCancel}
      theme={colorTheme as "light" | "dark"}
    />
    <CustomTimeframeModal
      isOpen={showTimeframeModal}
      onClose={() => setShowTimeframeModal(false)}
      theme={colorTheme as "light" | "dark"}
      onTimeframesChanged={() => window.dispatchEvent(new Event("custom-timeframes-changed"))}
    />
  </>
  );
};

export default GlobalChartToolbar;
